import { useState, useContext, useEffect, useMemo } from 'react';
import { I4goIdResponse, IAccessDetailsResponse } from '../../pages/preAuthenticationDetail/models/PreAuthModel';
import styles from './CreditCardI4GoIFrame.module.scss';
import { cssRules } from './CreditCardI4GoIFramestyle';
import PreAuthPageContext, { IPreAuthItemContext } from '../../Contexts/PreAuthPageContext/PreAuthPageContext';
import MenuItemsPageContext, { IMenuItemContext } from '../../Contexts/MenuItemsPageContext/MenuItemsPageContext';
import { COUNTRY_CODES_WITHOUT_ZIP_CODE, ZIP_CODE_COUNTRY_CODES } from '../../constants/constants';

declare global {
	interface JQuery {
		i4goTrueToken: (settings: unknown) => void;
	}
}

export interface AdditionalCardFields {
	isAvsPostalCodeRequired: boolean;
	isAvsBillingAddressRequired: boolean;
}

interface IProps {
	onSuccess: (data: I4goIdResponse) => void;
	onFailure: (data: I4goIdResponse) => void;
	accessDetails: IAccessDetailsResponse;
	i4goLoader: boolean;
	setI4goLoader: (i4goLoader: boolean) => void;
	setIsConsentModalVisible: (isConsentModalVisible: boolean) => void;

	// Hide or show postal code and street address for the credit card form
	showAdditionalCardField?: AdditionalCardFields | undefined;
}

const FOOTER__OFFSET_TOP_THRESHOLD = 460;

const CreditCardI4GoIFrame: React.FC<IProps> = (props) => {
	const isDebugEnabled = process.env.REACT_APP_ENVIRONMENT === 'development';

	const { i4goLoader, setI4goLoader, setIsConsentModalVisible, showAdditionalCardField } = props;
	const { setAccessDetails } = useContext(PreAuthPageContext) as IPreAuthItemContext;
	const { merchantData } = useContext(MenuItemsPageContext) as IMenuItemContext;
	const countryCode = (merchantData && merchantData?.countryCode) || 'US';
	const [isThreeDSFrameVisible, setIsThreeDSFrameVisible] = useState(false);

	function getCssRules(): string {
		let rules = cssRules;
	
		if (COUNTRY_CODES_WITHOUT_ZIP_CODE.includes(countryCode)) {
			rules += `
				.cvv2Codeinput {
					margin-bottom: 16px;
					position: relative;
				}
			`;
		}
	
		return rules;
	}
	

	useEffect(() => {
		const interval = setInterval(() => {
			const preAuthFooterPosition = document.getElementById('preAuthFooter')?.offsetTop || 0;
			if (preAuthFooterPosition > FOOTER__OFFSET_TOP_THRESHOLD) {
				clearInterval(interval);
				setI4goLoader(false);
				setIsConsentModalVisible(true);
				setAccessDetails({});
			}
		}, 500);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const postalCodeRequired = useMemo(() => {
		if (showAdditionalCardField && showAdditionalCardField.isAvsPostalCodeRequired) {
			return showAdditionalCardField.isAvsPostalCodeRequired && countryCode === ZIP_CODE_COUNTRY_CODES
		}

		return false;
	}, [showAdditionalCardField, countryCode])

	const streetAddressRequired = useMemo(() => {
		if (showAdditionalCardField && showAdditionalCardField.isAvsBillingAddressRequired) {
			return showAdditionalCardField.isAvsBillingAddressRequired && countryCode === ZIP_CODE_COUNTRY_CODES
		}

		return false;
	}, [showAdditionalCardField, countryCode])

	useEffect(() => {
		$('#i4goFrame').i4goTrueToken({
			server: props.accessDetails?.server,
			accessBlock: props.accessDetails?.accessBlock,
			url: process.env.I4GO_HOST,
			self: document.location.href,
			template: 'plain',
			threeDSecure: {
				challengeMode: 'iframe',
				iframeElementId: 'threeDsFrame',
				onOtpChallengeOpen: () => {
					setIsThreeDSFrameVisible(true);
				},
				onOtpChallengeClose: () => {
					setIsThreeDSFrameVisible(false);
				},
			},
			onSuccess: (_form: unknown, data: I4goIdResponse) => {
				return props.onSuccess(data);
			},
			onFailure: (_form: unknown, data: I4goIdResponse) => {
				return props.onFailure(data);
			},
			cardType: {
				classes: '',
				label: '',
				placeholder: '',
				message: '',
				visible: false,
			},
			cardholderName: {
				classes: 'cardholderName',
				placeholder: 'Name on the card',
				message: '',
				visible: true,
				required: true,
			},
			cardNumber: {
				classes: 'cardNumber',
				label: '',
				placeholder: '•••• •••• •••• ••••',
				message: '',
				visible: true,
				required: true,
			},
			cvv2Code: {
				classes: 'cvv2Codeinput',
				label: '',
				placeholder: '3 digit security code',
				message: '',
				visible: true,
				required: true,
			},
			postalCode: {
				classes: streetAddressRequired ? 'postalCode': 'postalCodezipcode',
				label: 'Postal Code',
				placeholder: 'Enter postal code',
				message: 'Invalid postal code',
				visible: postalCodeRequired,
				required: postalCodeRequired,
			},
			streetAddress: {
				classes: 'streetAddress',
				label: '',
				placeholder: 'Billing street address',
				message: '',
				visible: streetAddressRequired,
				required: streetAddressRequired,
			},
			submitButton: {
				classes: !postalCodeRequired && !streetAddressRequired && 'submitButton',
				label: 'Continue',
			},
			cssRules: [getCssRules()],
			debug: isDebugEnabled,
			remoteDebug: isDebugEnabled,
		});
	});

	return (
		<>
			{ isThreeDSFrameVisible && (
				<div id="threeDsFrame" className={`${styles.i4goFrame} ${styles.i4goThreeDsFrame}`} />
			)}

			<div id="i4goFrame" className={	isThreeDSFrameVisible ? styles.i4goFrameHide : styles.i4goFrame} />

			{ i4goLoader && (
				<div className={styles.i4goLoaderContainer}>
					<div className={styles.loader}></div>
				</div>
			)}
		</>
	);
};

export default CreditCardI4GoIFrame;
